import React, { useEffect, useState } from 'react';
import Yup from '~yup';
import { useFormik } from 'formik';

import Text from '~components/atoms/Text';
import Checkbox from '~components/atoms/Checkbox';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import { TDropdownOption } from '~types/components/labelledDropdown';
import { TGetInventoryListing, TGetInventoryListingFilters, TInventoryFilter, TTransferInventoryParams } from '~types/feat/gold';
import { BranchStatusEnum, FundTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum } from '~utils/enums';
import { GoldSilverInventorySelectedTab } from '~types/redux/state';

import * as FormStyles from './_transferInventoryForm.module.scss';

// ? validation
export const getTransferInventorySchema = (
    branchesList: TDropdownOption[],
    selectedFromBranch?: string,
    selectedToBranch?: string,
) => Yup.object().shape({
    branchFromCode: Yup.string().required('Current Branch Code is required'),
    branchFromName: Yup.string()
        .matchNoLeadingAndTrailingWhiteSpaces()
        .matchMaxOneWhiteSpace()
        .required('Current Branch name is required')
        .test('check-branch-name',
            'Selected branch does not exist',
            function (value) {
                if (!value) return false;
                return branchesList.some((branch) => branch.label === value);
            }
        ),
    branchToCode: Yup.string().required('Target Branch Code is required'),
    branchToName: Yup.string()
        .matchNoLeadingAndTrailingWhiteSpaces()
        .matchMaxOneWhiteSpace()
        .required('Target Branch name is required')
        .test('check-branch-name',
            'Selected branch does not exist',
            function (value) {
                if (!value) return false;
                return branchesList.some((branch) => branch.label === value);
            }
        )
        .test(
            'check-branch',
            'Cannot transfer to the same branch',
            function () {
                if (!selectedFromBranch) return false;
                if (!selectedToBranch) return false;
                return selectedFromBranch !== selectedToBranch;
            }
        ),
    productId: Yup.string().required('ProductID is required'),
    productName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Product Name is required'),
    amount: Yup.number().typeError('Quantity must be a number').positive('Quantity must be positive').required('Amount is required'),
    serialNo: Yup.array().of(Yup.string()),
});

const TransferInventoryForm = () => {

    const dispatch = useAppDispatch();

    const selectedInventoryTab = useAppSelector((state) => state.gold.goldInventory.inventoryListing.selectedTab);

    const isGoldTab = [
        GoldSilverInventorySelectedTab.GoldIslamicPaper,
        GoldSilverInventorySelectedTab.GoldIslamicPhysical,
        GoldSilverInventorySelectedTab.GoldConventionalPaper,
        GoldSilverInventorySelectedTab.GoldConventionalPhysical,
    ].includes(selectedInventoryTab);

    const isSilverTab = [
        GoldSilverInventorySelectedTab.SilverIslamicPaper,
        GoldSilverInventorySelectedTab.SilverIslamicPhysical,
        GoldSilverInventorySelectedTab.SilverConventionalPaper,
        GoldSilverInventorySelectedTab.SilverConventionalPhysical,
    ].includes(selectedInventoryTab);

    const getInitialMetalType = () => {
        if (isGoldTab) return MetalTypeEnum.Gold;
        return MetalTypeEnum.Silver;
    };

    const isPhysicalInventoryItem = (inventory?: TGetInventoryListing) => {
        if (!inventory) return false;
        if (isGoldTab) return inventory.goldType === GoldTypeEnum.PhysicalGold;
        if (isSilverTab) return inventory.physicalSilverWeight !== null;
        return false;
    };

    const branchesList = useAppSelector((state) => state.branch.allBranches);
    const goldSilverProducts = useAppSelector((state) => state.gold.goldInventory.inventoryListing);
    const productList = goldSilverProducts;

    const serialNumberList = useAppSelector((state) => state.gold.allSerialNumbers);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const inventoryFilterParams = useAppSelector((state) => state.gold.params.inventoryFilterParams);
    const serialNumberFilterParams = useAppSelector((state) => state.gold.params.serialNumberFilterParams);

    const isLoading = useAppSelector((state) => state.gold.loadings.transferInventory);

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const transferToBranchOptions = branchesList.filter(branch => branch.branchCode !== branchCode && branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const filterInventoryBySelectedTab = () => {
        const inventoryData = productList?.data ?? [];

        const isIslamic = [
            GoldSilverInventorySelectedTab.GoldIslamicPaper,
            GoldSilverInventorySelectedTab.GoldIslamicPhysical,
            GoldSilverInventorySelectedTab.SilverIslamicPaper,
            GoldSilverInventorySelectedTab.SilverIslamicPhysical,
        ].includes(selectedInventoryTab);

        const isPhysical = [
            GoldSilverInventorySelectedTab.GoldIslamicPhysical,
            GoldSilverInventorySelectedTab.GoldConventionalPhysical,
            GoldSilverInventorySelectedTab.SilverIslamicPhysical,
            GoldSilverInventorySelectedTab.SilverConventionalPhysical,
        ].includes(selectedInventoryTab);

        const filtered = inventoryData.filter(inventory => {
            const fundMatch = isIslamic
                ? inventory.fundType === FundTypeEnum.Islamic
                : inventory.fundType === FundTypeEnum.Conventional;

            let typeMatch = false;

            if (isGoldTab) {
                typeMatch = isPhysical
                    ? inventory.goldType === GoldTypeEnum.PhysicalGold
                    : inventory.goldType === GoldTypeEnum.PaperGold;
            }

            if (isSilverTab) {
                typeMatch = isPhysical
                    ? inventory.silverType === SilverTypeEnum.PhysicalSilver
                    : inventory.silverType === SilverTypeEnum.PaperSilver;
            }

            return fundMatch && typeMatch;
        });

        const uniqueByProductId = Array.from(
            new Map(filtered.map((inventory) => [inventory.productId, inventory])).values()
        );

        return uniqueByProductId.map(inventory => ({
            label: inventory.productName,
            value: inventory.productId,
        }));

        // return filtered.map(inventory => ({
        //     label: inventory.productName,
        //     value: inventory.productId,
        // }));
    };

    const productOptions = filterInventoryBySelectedTab();

    const TransferInventoryInitialValues = {
        branchFromCode: allBranchAccess ? '' : (branchesList?.find(branch => branch.branchCode === branchCode)?.branchCode || ''),
        branchFromName: allBranchAccess ? '' : (branchesList?.find(branch => branch.branchCode === branchCode)?.branchName || ''),
        branchToCode: '',
        branchToName: '',
        productId: 0,
        productName: '',
        amount: '',
        serialNo: [] as string[],
    };

    const [selectedBranchFromCode, setSelectedBranchFromCode] = useState<string>('');
    const [selectedBranchToCode, setSelectedBranchToCode] = useState<string>('');
    const [selectedProduct, setSelectedProduct] = useState<TGetInventoryListing>();
    const [selectedSerialNumber, setSelectedSerialNumber] = useState<string[]>([]);

    const formik = useFormik({
        initialValues: TransferInventoryInitialValues,
        validationSchema: getTransferInventorySchema(branchOptions, selectedBranchFromCode, selectedBranchToCode),
        onSubmit: values => {
            handleOnSubmit({
                ...values,
                metalType: getInitialMetalType(),
            });
        },
    });

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess){

            const lockedBranch = branchesList.find(branch => branch.branchCode === branchCode);
            if (lockedBranch) {
                const branch = {
                    value: lockedBranch.branchCode,
                    label: lockedBranch.branchName,
                };
                formik.setFieldValue('branchFromCode', lockedBranch?.branchCode);
                handleOnSelectBranchFrom(branch);
                formik.setFieldValue('branchFromName', lockedBranch?.branchName);
            }
        }
    }, [branchesList]);

    useEffect(() => {
        if (selectedBranchFromCode) {
            dispatch(combinedActions.goldSetInventoryFilterParams({ ...inventoryFilterParams, branchCode: selectedBranchFromCode, metalType: getInitialMetalType() }));
            dispatch(combinedActions.goldSetSerialNumberFilterParams({ ...serialNumberFilterParams, branchCode: selectedBranchFromCode, action: 1 })); // TODO: later need to adjust params
            dispatch(combinedActions.goldGetAllInventoriesAttempt());
            setSelectedProduct(undefined);
            formik.setValues({
                ...formik.values,
                productId: 0,
                productName: '',
                amount: '',
                serialNo: [],
            }); // to reset value each time changing branch
        }
    }, [selectedBranchFromCode]);

    useEffect(() => {
        if (selectedBranchToCode) {
            dispatch(combinedActions.goldGetBranchInventoriesAttempt({ branchCode: selectedBranchToCode }));
            setSelectedProduct(undefined);
            formik.setValues({
                ...formik.values,
                productId: 0,
                productName: '',
                amount: '',
                serialNo: [],
            });
        }
    }, [selectedBranchToCode]);

    useEffect(() => {
        if (selectedProduct) {
            setSelectedSerialNumber([]);
            dispatch(combinedActions.goldClearAllSerialNumbers());
            dispatch(combinedActions.goldSetSerialNumberFilterParams({ ...serialNumberFilterParams, productId: selectedProduct.productId }));
            dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
            formik.setValues({
                ...formik.values,
                amount: '',
                serialNo: [],
            });
        }
    }, [selectedProduct]);

    useEffect(() => {
        if (selectedSerialNumber.length > 0) {
            formik.setValues({
                ...formik.values,
                amount: selectedSerialNumber.length.toString(),
                serialNo: selectedSerialNumber,
            });
        } else {
            formik.setValues({
                ...formik.values,
                amount: '',
                serialNo: [],
            });
        }
    }, [selectedSerialNumber]);

    useEffect(() => {
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchFromName);

        if (newBranch) {
            formik.setFieldValue('branchFromCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchFromCode', '');
        }
    }, [formik.values.branchFromName]);

    useEffect(() => {
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchToName);

        if (newBranch) {
            formik.setFieldValue('branchToCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchToCode', '');
        }
    }, [formik.values.branchToName]);

    // const currentSerialNumberList = React.useMemo(
    //     () =>
    //         serialNumberList.find(
    //             (group) => group[0]?.productId === selectedProduct?.productId
    //         ) ?? [],
    //     [serialNumberList, selectedProduct],
    // );

    const handleOnSearchBranchFrom = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('branchFromName', event.target.value);
    };

    const handleOnSelectBranchFrom = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'branchFromCode', value: option.value } });
        setSelectedBranchFromCode(option.value as string);
    };

    const handleOnSearchBranchTo = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('branchToName', event.target.value);
    };

    const handleOnSelectBranchTo = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'branchToCode', value: option.value } });
        setSelectedBranchToCode(option.value as string);
    };

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'productName', value: event.target.value } });

        const product = productList?.data.find((productOption) => productOption.productName === event.target.value);

        if (!product) {
            setSelectedProduct(undefined);
            return;
        }
        setSelectedProduct(product);
        formik.setValues({
            ...formik.values,
            productId: Number(product.productId),
            productName: product.productName,
        });
    };

    const handleOnSelectProduct = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'productName', value: option.value } });

        const product = productList?.data.find((productOption) => productOption.productId === option.value);

        if (!product) {
            setSelectedProduct(undefined);
            return;
        }
        setSelectedProduct(product);
        formik.setValues({
            ...formik.values,
            productId: Number(product.productId),
            productName: product.productName,
        });
    };

    const handleOnSelectSerialNumber = (serialNumber: string) => {
        if (selectedSerialNumber.includes(serialNumber)) {
            setSelectedSerialNumber(selectedSerialNumber.filter((item) => item !== serialNumber));
        } else {
            setSelectedSerialNumber([...selectedSerialNumber, serialNumber]);
        }
    };

    const handleOnSelectAllSerialNumber = () => {
        // const allSerialNos = currentSerialNumberList.map((serialNumber) => serialNumber.serialNo);

        if (selectedSerialNumber.length === serialNumberList.length) {
            setSelectedSerialNumber([]);
        } else {
            setSelectedSerialNumber(serialNumberList.map((serialNumber) => serialNumber.serialNo));
        }
    };

    const handleOnSubmit = (data: TTransferInventoryParams) => {
        dispatch(combinedActions.goldSetTransferInventoryParams(data));
        dispatch(combinedActions.goldTransferInventoryAttempt());
    };

    const renderSerialNumbers = () => {
        if (serialNumberList && serialNumberList.length > 0) {
            return serialNumberList.map(serialNumber => {
                return (
                    <div
                        className={FormStyles['serial-number']}
                        key={serialNumber.serialNo}
                    >
                        <Checkbox
                            isReversed
                            label={serialNumber.serialNo}
                            isChecked={selectedSerialNumber.includes(
                                serialNumber.serialNo
                            )}
                            onChange={() =>
                                handleOnSelectSerialNumber(
                                    serialNumber.serialNo
                                )
                            }
                        />
                    </div>
                );
            });
        }

        return null;
    };

    const renderSerialNumberList = () => {
        if (isPhysicalInventoryItem(selectedProduct)) {
            return (
                <div className={FormStyles['serial-container']}>
                    <Text
                        isBold
                        label={'Serial Numbers* :'}
                        className={FormStyles['title']}
                    />

                    <div className={FormStyles['serial-list']}>
                        <Checkbox
                            isReversed
                            label='Select All'
                            isChecked={selectedSerialNumber.length === serialNumberList.length && serialNumberList.length > 0}
                            onChange={handleOnSelectAllSerialNumber}
                        />

                        {renderSerialNumbers()}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
        >
            <div className={FormStyles['form-container']}>

                <LabelledDropdown
                    id='branchFromName'
                    name='branchFromName'
                    label='Current Branch* :'
                    options={branchOptions}
                    value={formik.values.branchFromName}
                    error={formik.touched.branchFromName ? formik.errors.branchFromName : undefined}
                    onChange={handleOnSearchBranchFrom}
                    onSelectOption={handleOnSelectBranchFrom}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!branchOptions.find((branch) => branch.value === formik.values.branchFromCode)}
                    disabled={!allBranchAccess}
                />

                <LabelledDropdown
                    id='branchToName'
                    name='branchToName'
                    label='Target Branch* :'
                    options={transferToBranchOptions}
                    value={formik.values.branchToName}
                    error={formik.touched.branchToName ? formik.errors.branchToName : undefined}
                    onChange={handleOnSearchBranchTo}
                    onSelectOption={handleOnSelectBranchTo}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!transferToBranchOptions.find((branch) => branch.value === formik.values.branchToCode)}
                    disabled={!selectedBranchFromCode}
                />

                <LabelledDropdown
                    id='productName'
                    name='productName'
                    label='Product Name* :'
                    options={productOptions.sort((a, b) => Number(a.value) - Number(b.value))}
                    value={formik.values.productName}
                    error={formik.touched.productName ? formik.errors.productName : undefined}
                    onChange={handleOnSearchProduct}
                    onSelectOption={handleOnSelectProduct}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No product found'
                    placeholder='Select current branch first'
                    autoComplete='off'
                    optionSelected={!!productOptions.find((product) => product.label === formik.values.productName)}
                    disabled={!selectedBranchFromCode}
                />

                <LabelledInput
                    id='amount'
                    name='amount'
                    label='Quantity* :'
                    value={formik.values.amount}
                    error={formik.touched.amount ? formik.errors.amount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!selectedProduct || selectedProduct.goldType === GoldTypeEnum.PhysicalGold || selectedProduct.silverType === SilverTypeEnum.PhysicalSilver}
                />
            </div>

            {renderSerialNumberList()}

            <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                <div className={FormStyles['button-subcontainer']}>
                    <ActionButton
                        label='Transfer Inventory'
                        type='submit'
                        isLoading={isLoading}
                        disabled={isLoading}
                    />
                </div>
            </div>
        </form>
    );
};

export default TransferInventoryForm;
